import React, { useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Button, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material'
import { H3 } from '../../layouts/TextStyles'
import { tone, ui } from '../../utils/uiTokens'
import { capitalize } from '../../utils/functions'
import { MySurveyItem, SurveyAnswerBody, SurveyQuestion, surveyApi } from '../../features/survey/surveyApi'
import { Pill, bodyRow, btn, headRow, muted, page, sub, tableWrap } from '../director/ui'
import { btnPink } from './SurveysPage'
import { ddmm } from './surveyTemplates'

const MIN_TEXT = 5
const outlined = { ...btn, color: ui.ink, borderColor: ui.border }
const headName = (i: MySurveyItem) => `${i.mentorFirstName ?? ''} ${i.mentorLastName ?? ''}`.trim() || `#${i.mentorUserId}`

// Куратор заполняет опрос о своём пән басшы: список открытых анкет и сама анкета (?surveyId=&mentorUserId=)
const CuratorSurveysPage = () => {
    const { formatMessage, locale } = useIntl()
    const L = capitalize(locale)
    const [params, setParams] = useSearchParams()
    const surveyId = Number(params.get('surveyId')) || 0
    const mentorUserId = Number(params.get('mentorUserId')) || 0
    const { data: items = [], isFetching } = surveyApi.useGetCuratorSurveysQuery()

    if (surveyId && mentorUserId) {
        return <SurveyForm surveyId={surveyId} mentorUserId={mentorUserId} onBack={() => setParams({}, { replace: true })} />
    }

    return (
        <Box sx={{ margin: '10px' }}>
            <Box sx={page}>
                <H3>{formatMessage({ id: 'surveyFillTitle' })}</H3>
                <Box sx={{ ...sub, margin: '6px 0 16px', maxWidth: 760 }}>{formatMessage({ id: 'surveyFillSub' })}</Box>
                {isFetching && <LinearProgress />}
                <Box sx={tableWrap}>
                    <Table sx={{ minWidth: 640 }}>
                        <TableHead sx={headRow}>
                            <TableRow>
                                <TableCell>{formatMessage({ id: 'surveyColTitle' })}</TableCell>
                                <TableCell>{formatMessage({ id: 'surveyTarget_SUBJECT_HEAD' })}</TableCell>
                                <TableCell align="center">{formatMessage({ id: 'surveyCloseDate' })}</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((i) => (
                                <TableRow key={`${i.surveyId}-${i.mentorUserId}`} hover sx={bodyRow}>
                                    <TableCell><b>{(i as any)[`title${L}`] || i.titleRu}</b></TableCell>
                                    <TableCell>
                                        {headName(i)}
                                        <Box sx={muted}>{(i as any)[`subjectTitle${L}`] || i.subjectTitleRu}</Box>
                                    </TableCell>
                                    <TableCell align="center">{ddmm(i.closeDate)}</TableCell>
                                    <TableCell align="right">
                                        {i.answered ? (
                                            <Pill tone="good">{formatMessage({ id: 'surveySent' })}</Pill>
                                        ) : (
                                            <Button size="small" variant="contained" sx={btnPink}
                                                onClick={() => setParams({ surveyId: String(i.surveyId), mentorUserId: String(i.mentorUserId) })}>
                                                {formatMessage({ id: 'surveyFill' })}
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!isFetching && !items.length && (
                                <TableRow sx={bodyRow}>
                                    <TableCell colSpan={4} align="center" sx={{ color: ui.muted }}>{formatMessage({ id: 'surveyFillNone' })}</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
                <StudentStatus />
            </Box>
        </Box>
    )
}

// Кто из учеников ментора прошёл открытые опросы о менторе и учителе - без ответов
const StudentStatus = () => {
    const { formatMessage, locale } = useIntl()
    const L = capitalize(locale)
    const { data: surveys = [], isFetching } = surveyApi.useGetMentorSurveyStatusQuery()
    const name = (x: { firstName: string | null; lastName: string | null; studentUserId: number }) =>
        `${x.lastName ?? ''} ${x.firstName ?? ''}`.trim() || `#${x.studentUserId}`

    return (
        <Box sx={{ marginTop: '32px' }}>
            <H3>{formatMessage({ id: 'surveyStatusTitle' })}</H3>
            <Box sx={{ ...sub, margin: '6px 0 16px', maxWidth: 760 }}>{formatMessage({ id: 'surveyStatusSub' })}</Box>
            {isFetching && <LinearProgress />}
            {!isFetching && !surveys.length && <Box sx={muted}>{formatMessage({ id: 'surveyStatusNone' })}</Box>}
            {surveys.map((sv) => {
                const done = sv.students.filter((x) => x.answered).length
                const list = [...sv.students].sort((a, b) => Number(a.answered) - Number(b.answered) || name(a).localeCompare(name(b)))
                return (
                    <Box key={sv.surveyId} sx={{ marginBottom: '16px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', marginBottom: '8px' }}>
                            <b>{(sv as any)[`title${L}`] || sv.titleRu}</b>
                            <Box component="span" sx={muted}>
                                {formatMessage({ id: `surveyTarget_${sv.type}` })} · {formatMessage({ id: 'surveyCloseDate' })} {ddmm(sv.closeDate)}
                            </Box>
                            <Pill tone={done === sv.students.length ? 'good' : 'warn'}>
                                {formatMessage({ id: 'surveyStatusCount' }, { done, total: sv.students.length })}
                            </Pill>
                        </Box>
                        <Box sx={tableWrap}>
                            <Table size="small">
                                <TableBody>
                                    {list.map((x) => (
                                        <TableRow key={x.studentUserId} sx={bodyRow}>
                                            <TableCell>{name(x)}</TableCell>
                                            <TableCell align="right">
                                                <Pill tone={x.answered ? 'good' : 'bad'}>
                                                    {formatMessage({ id: x.answered ? 'surveyStatusDone' : 'surveyStatusNotDone' })}
                                                </Pill>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Box>
                )
            })}
        </Box>
    )
}

type Ans = { rating?: number; choice?: number; text?: string; nps?: number }

const isAnswered = (q: SurveyQuestion, a: Ans | undefined) => {
    if (!a) return false
    if (q.kind === 'RATING') return !!a.rating
    if (q.kind === 'CHOICE') return !!a.choice
    if (q.kind === 'NPS') return a.nps != null
    return (a.text ?? '').trim().length >= MIN_TEXT
}

const SurveyForm: React.FC<{ surveyId: number; mentorUserId: number; onBack: () => void }> = ({ surveyId, mentorUserId, onBack }) => {
    const { formatMessage, locale } = useIntl()
    const L = capitalize(locale)
    const { data, isFetching } = surveyApi.useGetCuratorSurveyQuery({ surveyId, mentorUserId })
    const [submit, { isLoading }] = surveyApi.useSubmitCuratorSurveyMutation()
    const [answers, setAnswers] = useState<Record<number, Ans>>({})
    const [bad, setBad] = useState<number[]>([])
    const refs = useRef<Record<number, HTMLDivElement | null>>({})
    const questions = data?.questions ?? []

    const set = (qid: number, patch: Ans) => {
        setAnswers((a) => ({ ...a, [qid]: { ...a[qid], ...patch } }))
        setBad((b) => b.filter((x) => x !== qid))
    }

    const highlight = (ids: number[]) => {
        setBad(ids)
        const first = questions.find((q) => q.id != null && ids.includes(q.id))
        if (first?.id != null) refs.current[first.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    const done = questions.filter((q) => q.id != null && isAnswered(q, answers[q.id])).length

    const send = async () => {
        const missing = questions.filter((q) => q.id != null && !isAnswered(q, answers[q.id])).map((q) => q.id as number)
        if (missing.length) return highlight(missing)
        const body: SurveyAnswerBody[] = questions.map((q) => {
            const a = answers[q.id as number] ?? {}
            return {
                questionId: q.id as number,
                rating: q.kind === 'RATING' ? a.rating ?? null : null,
                choice: q.kind === 'CHOICE' ? a.choice ?? null : null,
                text: q.kind === 'TEXT' ? (a.text ?? '').trim() : null,
                nps: q.kind === 'NPS' ? a.nps ?? null : null,
            }
        })
        try {
            await submit({ surveyId, mentorUserId, answers: body }).unwrap()
            toast.success(formatMessage({ id: 'surveyThanks' }))
            onBack()
        } catch (e: any) {
            // 422 SURVEY_INCOMPLETE: field = id вопросов без ответа через запятую
            const field: string | undefined = e?.data?.errors?.[0]?.field
            if (e?.status === 422 && field) highlight(field.split(',').map(Number).filter(Boolean))
        }
    }

    const scale = (qid: number, from: number, to: number, value: number | undefined, key: 'rating' | 'nps') => (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', marginTop: '10px' }}>
            {Array.from({ length: to - from + 1 }, (_, k) => from + k).map((v) => (
                <Button key={v} size="small" variant={value === v ? 'contained' : 'outlined'} onClick={() => set(qid, { [key]: v })}
                    sx={{ ...(value === v ? btnPink : outlined), minWidth: 40, padding: '5px 0' }}>
                    {v}
                </Button>
            ))}
        </Box>
    )

    const item = data?.item

    return (
        <Box sx={{ margin: '10px' }}>
            <Box sx={page}>
                <Button size="small" sx={{ ...btn, color: ui.sub, marginBottom: '8px' }} onClick={onBack}>‹ {formatMessage({ id: 'surveyBackToList' })}</Button>
                {isFetching && <LinearProgress />}
                {item && (
                    <>
                        <H3>{(item as any)[`title${L}`] || item.titleRu}</H3>
                        <Box sx={{ ...sub, margin: '6px 0 16px' }}>
                            {headName(item)} · {(item as any)[`subjectTitle${L}`] || item.subjectTitleRu}
                            <Box sx={muted}>{formatMessage({ id: 'surveyFillHint' }, { n: MIN_TEXT })}</Box>
                        </Box>
                        {item.answered ? (
                            <Pill tone="good">{formatMessage({ id: 'surveySent' })}</Pill>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 760 }}>
                                {questions.map((q, i) => {
                                    const qid = q.id as number
                                    const a = answers[qid]
                                    const isBad = bad.includes(qid)
                                    const text = (a?.text ?? '').trim()
                                    return (
                                        <Box key={qid} ref={(el: HTMLDivElement | null) => { refs.current[qid] = el }}
                                            sx={{
                                                border: `1px solid ${isBad ? tone.bad.border : ui.border}`,
                                                background: isBad ? tone.bad.bg : ui.surface,
                                                borderRadius: ui.radius, padding: '12px 14px',
                                            }}>
                                            <Box sx={{ fontSize: '14px', fontWeight: 600, color: ui.ink }}>
                                                {i + 1}. {(q as any)[`text${L}`] || q.textRu}
                                            </Box>
                                            {q.kind === 'RATING' && scale(qid, 1, 5, a?.rating, 'rating')}
                                            {q.kind === 'NPS' && scale(qid, 0, 10, a?.nps, 'nps')}
                                            {q.kind === 'CHOICE' && (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, marginTop: '10px', alignItems: 'flex-start' }}>
                                                    {q.options.map((o, k) => (
                                                        <Button key={k} size="small" variant={a?.choice === k + 1 ? 'contained' : 'outlined'}
                                                            sx={{ ...(a?.choice === k + 1 ? btnPink : outlined), whiteSpace: 'normal', textAlign: 'left' }}
                                                            onClick={() => set(qid, { choice: k + 1 })}>
                                                            {(o as any)[`text${L}`] || o.textRu}
                                                        </Button>
                                                    ))}
                                                </Box>
                                            )}
                                            {q.kind === 'TEXT' && (
                                                <TextField size="small" fullWidth multiline minRows={3} sx={{ marginTop: '10px', background: ui.card }}
                                                    value={a?.text ?? ''} onChange={(e) => set(qid, { text: e.target.value })}
                                                    helperText={text.length > 0 && text.length < MIN_TEXT ? formatMessage({ id: 'surveyTextShort' }, { n: MIN_TEXT }) : undefined} />
                                            )}
                                            {isBad && <Box sx={{ ...muted, color: tone.bad.text, marginTop: '6px' }}>{formatMessage({ id: 'surveyAnswerRequired' })}</Box>}
                                        </Box>
                                    )
                                })}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', marginTop: '4px' }}>
                                    <Button variant="contained" sx={btnPink} disabled={isLoading} onClick={send}>{formatMessage({ id: 'surveySend' })}</Button>
                                    <Box sx={muted}>{formatMessage({ id: 'surveyProgress' }, { done, n: questions.length })}</Box>
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    )
}

export default CuratorSurveysPage
